import type { TelegramClient } from "telegram";
import { loadTg } from "./tglib";
import { sessionStore } from "./sessionStore";
import { installWsProxy } from "./wsProxy";

let client: TelegramClient | null = null;

export interface ConnectResult {
  client: TelegramClient;
  authorized: boolean;
}

/**
 * Build (once) and connect the GramJS client over WSS. We force PromisedWebSockets
 * + useWSS so the browser uses wss://{dc}.web.telegram.org/apiws regardless of any
 * environment-detection quirks introduced by the node polyfills.
 */
export async function getClient(): Promise<TelegramClient> {
  if (client) return client;
  const apiId = await sessionStore.getApiId();
  const apiHash = await sessionStore.getApiHash();
  if (!apiId || !apiHash) throw new Error("API credentials not set");

  if (await sessionStore.getProxyEnabled()) installWsProxy();

  const { TelegramClient, StringSession, PromisedWebSockets } = await loadTg();
  const saved = (await sessionStore.getSession()) ?? "";
  const session = new StringSession(saved);
  client = new TelegramClient(session, apiId, apiHash, {
    connectionRetries: 5,
    useWSS: true,
    networkSocket: PromisedWebSockets,
    autoReconnect: true,
    floodSleepThreshold: 60,
    requestRetries: 3,
    deviceModel: "Web",
    appVersion: "1.0",
    systemVersion: "web",
  });
  return client;
}

/** Connect and report whether the saved session is already authorized. */
export async function connect(): Promise<ConnectResult> {
  const c = await getClient();
  if (!c.connected) await c.connect();
  const authorized = await c.checkAuthorization().catch(() => false);
  return { client: c, authorized };
}

export async function persistSession(): Promise<void> {
  if (!client) return;
  await sessionStore.putSession(client.session.save() as unknown as string);
}

export async function disconnectClient(): Promise<void> {
  if (client) {
    try {
      await client.disconnect();
    } catch {
      /* ignore */
    }
    try {
      await client.destroy();
    } catch {
      /* ignore */
    }
  }
  client = null;
}

export function getExistingClient(): TelegramClient | null {
  return client;
}
